import { debug } from 'debug';
import { useState } from 'react';
import { createEventSourceHook } from '~/lib/create-event-source-hook';
import { dropStatusEventSchema, type DropStatusEvent } from '~/lib/drop-status-event.schema';
import { useLatestValueRef } from '~/lib/use-latest-value-ref';
import { useAuthStore } from '~/store';

interface UseDropStatusStreamProps {
  dropId: string;
  onDropCompleted: () => void;
  enabled?: boolean;
}

const logger = debug('app:drop-status-stream');

const useDropStatusEventSource = createEventSourceHook<DropStatusEvent>({
  parseMessage: (data: string) => {
    try {
      const parsedData = JSON.parse(data) as unknown;
      return dropStatusEventSchema.parse(parsedData);
    } catch {
      return null;
    }
  },
});

export const useDropStatusStream = ({ dropId, enabled = true, onDropCompleted }: UseDropStatusStreamProps) => {
  const [status, setStatus] = useState<string | null>(null);

  const latestRef = useLatestValueRef({ onDropCompleted });

  const { isConnected, error, disconnect } = useDropStatusEventSource({
    pathname: `/stream/drops/${dropId}/status`,
    getAccessToken: () => useAuthStore.getState().accessToken,
    enabled: enabled && Boolean(dropId),
    onMessage: (data) => {
      logger('Drop status', data);
      setStatus(data.status);

      if (data.status === 'completed') {
        logger('Drop completed.');
        latestRef.current.onDropCompleted();
      }
    },
  });

  return {
    status,
    isConnected,
    error,
    disconnect,
  };
};
